import { useEffect, useState } from "react";

function getQuoteText(quote = {}) {
  return quote?.quote_text || quote?.quoteText || "";
}

function getQuoteValidation(quote = {}, quoteValidation) {
  return (
    quoteValidation ||
    quote?.quoteValidation ||
    quote?.quote_validation ||
    quote?.quote_text_validation ||
    null
  );
}

function getQuoteId(quote = {}) {
  return quote?.id || quote?.quote_id || quote?.quoteId || null;
}

export default function QuoteTextWorkspace({
  quote,
  quoteValidation,
  onRegenerateQuoteText,
  onSaveQuoteText,
  isBusy = false,
}) {
  const initialQuoteText = getQuoteText(quote);
  const validation = getQuoteValidation(quote, quoteValidation);
  const quoteId = getQuoteId(quote);

  const [quoteText, setQuoteText] = useState(initialQuoteText);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setQuoteText(initialQuoteText);
  }, [initialQuoteText]);

  const hasValidation = !!validation;
  const hasIssues = validation?.isValid === false;
  const issues = Array.isArray(validation?.issues) ? validation.issues : [];
  const hasUnsavedChanges = quoteText !== initialQuoteText;

  async function handleSaveQuoteText() {
    if (!quoteId && !onSaveQuoteText) return;

    setIsSaving(true);
    setSaveMessage("");
    setErrorMessage("");

    try {
      if (onSaveQuoteText) {
        await onSaveQuoteText(quoteText);
      } else {
        const response = await fetch(`/api/quotes/${quoteId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ quote_text: quoteText }),
        });

        if (!response.ok) throw new Error("Failed to save proposal text.");
      }

      setSaveMessage("Proposal text saved.");
    } catch (error) {
      setErrorMessage(error?.message || "Could not save proposal text.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="grid h-full grid-cols-[minmax(0,1fr)_340px] bg-[#f3f2f1] text-[#201f1e]">
      <main className="min-w-0 overflow-hidden border-r border-[#edebe9] bg-white">
        <div className="flex h-11 items-center justify-between border-b border-[#edebe9] bg-[#faf9f8] px-3">
          <div className="flex items-center gap-1">
            {quoteId && (
              <a
                href={`${
                  process.env.REACT_APP_API_BASE_URL || "http://localhost:5050"
                }/api/quotes/${quoteId}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="rounded-sm px-3 py-1.5 text-sm text-[#323130] hover:bg-[#f3f2f1]"
              >
                Export PDF
              </a>
            )}

            <button
              type="button"
              onClick={() => onRegenerateQuoteText?.()}
              disabled={isBusy || !onRegenerateQuoteText}
              className="rounded-sm px-3 py-1.5 text-sm text-[#323130] hover:bg-[#f3f2f1] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isBusy ? "Regenerating..." : "Regenerate"}
            </button>

            <button
              type="button"
              onClick={handleSaveQuoteText}
              disabled={isBusy || isSaving || !hasUnsavedChanges}
              className="rounded-sm px-3 py-1.5 text-sm font-medium text-[#201f1e] hover:bg-[#f3f2f1] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save text"}
            </button>
          </div>

          <span
            className={[
              "rounded-sm px-2 py-1 text-xs font-medium",
              hasUnsavedChanges
                ? "bg-[#fff4ce] text-[#8a6d1d]"
                : "bg-[#dff6dd] text-[#107c10]",
            ].join(" ")}
          >
            {hasUnsavedChanges ? "Unsaved changes" : "Saved"}
          </span>
        </div>

        <div className="border-b border-[#edebe9] bg-white px-5 py-4">
          <h2 className="text-base font-semibold text-[#201f1e]">
            Proposal Text
          </h2>

          <p className="mt-1 text-xs text-[#605e5c]">
            Customer-facing proposal generated from active estimate rows.
          </p>
        </div>

        <div className="h-[calc(100%-104px)] overflow-auto bg-[#f3f2f1] p-4">
          <textarea
            value={quoteText}
            onChange={(event) => setQuoteText(event.target.value)}
            rows={24}
            placeholder="No proposal text generated yet."
            className="min-h-[560px] w-full resize-y rounded-sm border border-[#edebe9] bg-white p-5 text-sm leading-6 text-[#323130] shadow-sm outline-none focus:border-[#0f6cbd] focus:ring-1 focus:ring-[#0f6cbd]"
          />

          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-[#605e5c]">
              Editable customer-facing proposal text.
            </span>

            <span className="text-[#605e5c]">
              Prices excl. VAT. VAT shown in final summary.
            </span>
          </div>
        </div>
      </main>

      <aside className="overflow-auto border-l border-[#edebe9] bg-[#f3f2f1]">
        <Panel title="Proposal validation">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-xs text-[#605e5c]">
              Alignment between proposal text and estimate data.
            </p>

            {hasValidation && (
              <span
                className={[
                  "rounded-sm border px-2 py-0.5 text-xs font-medium",
                  hasIssues
                    ? "border-[#f3d6a0] bg-[#fff4ce] text-[#8a6d1d]"
                    : "border-[#c7e0c0] bg-[#dff6dd] text-[#107c10]",
                ].join(" ")}
              >
                {hasIssues ? "Needs review" : "Validated"}
              </span>
            )}
          </div>

          {!hasValidation ? (
            <InfoBox>No validation result available.</InfoBox>
          ) : hasIssues ? (
            <div className="space-y-2">
              {issues.map((issue, index) => (
                <div
                  key={`${issue}-${index}`}
                  className="rounded-sm border border-[#f3d6a0] bg-[#fff4ce] px-3 py-2 text-sm text-[#8a6d1d]"
                >
                  {issue}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-sm border border-[#c7e0c0] bg-[#dff6dd] px-3 py-2 text-sm text-[#107c10]">
              No proposal text issues detected.
            </div>
          )}
        </Panel>

        <Panel title="Rules">
          <div className="space-y-2 text-sm text-[#605e5c]">
            <InfoBox>Only active rows should be described as included.</InfoBox>
            <InfoBox>Inactive rows should appear only as optional items.</InfoBox>
            <InfoBox>
              PDF should show subtotal excl. VAT, VAT amount and total incl. VAT.
            </InfoBox>
          </div>
        </Panel>

        <Panel title="Status">
          <div className="space-y-2 text-sm">
            <StatusRow label="Quote status" value={quote?.status || "draft"} />

            <StatusRow
              label="Updated"
              value={
                quote?.updated_at
                  ? new Date(quote.updated_at).toLocaleString()
                  : "—"
              }
            />

            <StatusRow
              label="Unsaved changes"
              value={hasUnsavedChanges ? "Yes" : "No"}
              tone={hasUnsavedChanges ? "warning" : "success"}
            />
          </div>

          {(saveMessage || quote?.save_message) && (
            <div className="mt-3 rounded-sm border border-[#c7e0c0] bg-[#dff6dd] px-3 py-2 text-sm text-[#107c10]">
              {saveMessage || quote.save_message}
            </div>
          )}

          {(errorMessage || quote?.error_message) && (
            <div className="mt-3 rounded-sm border border-[#f1aeb5] bg-[#fde7e9] px-3 py-2 text-sm text-[#a4262c]">
              {errorMessage || quote.error_message}
            </div>
          )}
        </Panel>
      </aside>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section className="m-3 overflow-hidden rounded-sm border border-[#edebe9] bg-white shadow-sm">
      <div className="border-b border-[#edebe9] bg-[#faf9f8] px-3 py-2">
        <h3 className="text-sm font-semibold text-[#201f1e]">{title}</h3>
      </div>

      <div className="p-3">{children}</div>
    </section>
  );
}

function InfoBox({ children }) {
  return (
    <div className="rounded-sm border border-[#edebe9] bg-[#faf9f8] px-3 py-2">
      {children}
    </div>
  );
}

function StatusRow({ label, value, tone }) {
  return (
    <div className="flex justify-between gap-3 border-b border-[#edebe9] pb-2 last:border-b-0">
      <span className="text-[#605e5c]">{label}</span>
      <span
        className={[
          "text-right font-medium",
          tone === "warning"
            ? "text-[#8a6d1d]"
            : tone === "success"
            ? "text-[#107c10]"
            : "text-[#323130]",
        ].join(" ")}
      >
        {value}
      </span>
    </div>
  );
}